import { ApplicationCountry, ApplicationType } from '~/constants/dashboard'
import steps from '~/constants/stepperData'

import { isDateGreaterThanToday } from './common'
import { LIGHT_SPEED_SEGMENTS, USER_ROLE } from './constant'
import { getObjectCookie } from './getObjectCookie'
import { UppercaseFirstLetter } from './uppercaseFirstLetter'

type UserDetails = {
  role: string
}
type Data = {
  userdetails?: UserDetails
}
type URLObject = {
  pathname?: string
}
export const handleRedirect = (url: string) => {
  if (url) {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank')
    } else {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}

type FlagMap = {
  [key: string]: string[]
}

export const filterSteps = (flagMap: FlagMap) => {
  try {
    if (!flagMap) return steps
    const stepsCopy = JSON.parse(JSON.stringify(steps))
    const shouldExclude = (route: any, flags: string[]): boolean => {
      return flags?.some((flag) => route[flag] === true)
    }
    const modifiedSteps = stepsCopy?.filter((parentRoute) => {
      const parentFlags = flagMap?.['parent'] || []
      if (shouldExclude(parentRoute, parentFlags)) return false
      if (parentRoute?.steps) {
        const childFlags = flagMap?.['child'] || []
        parentRoute.steps = parentRoute?.steps?.filter(
          (childRoute) => !shouldExclude(childRoute, childFlags),
        )
      }
      return true
    })
    return modifiedSteps || steps
  } catch (error) {
    return steps
  }
}

export const getLightSpeedFlags = (userDetails = {}) => {
  const { additional_fields = {}, show_lightspeed = false, data = {} } = userDetails || {}

  const enrollmentStatusOptions = [
    'I am currently enrolled at a school, but I am transferring to another school',
    'I am currently enrolled',
  ]

  const isEnrolledTransfer = enrollmentStatusOptions?.includes(data?.tellUsAboutYou)
  return {
    isNewUserInLightSpeedFlow: show_lightspeed,
    hasSubmittedLightSpeed: additional_fields?.is_lightspeed_submitted || false,
    isLightSpeedUser: LIGHT_SPEED_SEGMENTS.includes(additional_fields?.lightspeed_segment) || false,
    isCurrentlyEnrolledTransfer: isEnrolledTransfer,
    requiredUSEmploymentData: additional_fields?.requires_us_employment_details,
  }
}

export const getDefaultLightSpeedFlags = () => ({
  isNewUserInLightSpeedFlow: false,
  hasSubmittedLightSpeed: false,
  isLightSpeedUser: false,
  isCurrentlyEnrolledTransfer: false,
  requiredUSEmploymentData: false,
})

export const lightSpeedRelatedFlags = (userDetails: Record<string, never>) => {
  try {
    return getLightSpeedFlags(userDetails)
  } catch (error) {
    return getDefaultLightSpeedFlags()
  }
}

const redirectPaths = {
  reviewAndSubmit: '/finance/reviewAndSubmit',
  finance: '/finance',
  employment: '/employment',
  education: '/education',
  educationType: '/education/type',
}

export const getRedirectPath = (loaderActionData = {}, moduleData = {}, pageName) => {
  try {
    const { fields = {}, status = false } = loaderActionData || {}
    const { additional_fields = {}, borrower_step_progress = false } = moduleData || {}
    const { lightspeed_segment = false, is_lightspeed_submitted = false } = additional_fields
    const borrowerStpProgress = Number(loaderActionData?.fields?.borrowerStpProgress)
    const conditions = [
      {
        atomicRouteName: 'employment',
        condition: additional_fields?.requires_us_employment_details,
        path: '',
      },
      {
        atomicRouteName: 'employment',
        condition:
          LIGHT_SPEED_SEGMENTS.includes(lightspeed_segment) &&
          is_lightspeed_submitted &&
          Number(borrower_step_progress) === 2,
        path: redirectPaths.finance,
      },
      {
        atomicRouteName: 'employment',
        condition: fields?.isLightSpeedUser && !additional_fields?.is_lightspeed_submitted,
        path: redirectPaths.education,
      },
      {
        atomicRouteName: 'score',
        condition: fields?.isLightSpeedUser && status && Number(fields?.borrowerStpProgress) === 4,
        path: redirectPaths.education,
      },
      {
        atomicRouteName: 'test',
        condition:
          fields?.isNewUserInLightSpeedFlow &&
          !fields?.isLightSpeedUser &&
          status &&
          Number(fields?.borrowerStpProgress) === 4,
        path: redirectPaths.employment,
      },
      {
        atomicRouteName: 'type',
        condition: loaderActionData?.fields?.requiredUSEmploymentData,
        path: redirectPaths.employment,
      },
      {
        atomicRouteName: 'type',
        condition: fields?.isLightSpeedUser && status && Number(fields?.borrowerStpProgress) === 4,
        path: redirectPaths.reviewAndSubmit,
      },
      {
        atomicRouteName: 'type',
        condition: status,
        path: redirectPaths.finance,
      },
      {
        atomicRouteName: 'finance',
        condition: loaderActionData?.fields?.requiredUSEmploymentData,
        path: redirectPaths.employment,
      },
      {
        atomicRouteName: 'finance',
        condition: fields?.isLightSpeedUser && isDateGreaterThanToday(fields?.programStartDate),
        path: redirectPaths.education,
      },
      {
        atomicRouteName: 'finance',
        condition: fields?.isLightSpeedUser && isDateGreaterThanToday(fields?.programStartDate),
        path: redirectPaths.education,
      },
      {
        atomicRouteName: 'finance',
        condition: fields?.isLightSpeedUser,
        path: redirectPaths.educationType,
      },
      {
        atomicRouteName: 'addUserFunding',
        condition:
          loaderActionData?.isLightSpeedUser &&
          isDateGreaterThanToday(loaderActionData?.programStartDate),
        path: redirectPaths.education,
      },
      {
        atomicRouteName: 'addUserFunding',
        condition: loaderActionData?.isLightSpeedUser,
        path: redirectPaths.educationType,
      },
      {
        atomicRouteName: 'reviewAndSubmit',
        condition: fields?.isLightSpeedUser && !additional_fields?.is_lightspeed_submitted,
        path: redirectPaths.education,
      },
      {
        atomicRouteName: 'reviewAndSubmit',
        condition: additional_fields?.is_lightspeed_submitted && !Object.keys(moduleData).length,
        path: redirectPaths.employment,
      },
    ]
    for (const { condition, path, atomicRouteName } of conditions) {
      if (pageName == atomicRouteName && condition) {
        return path
      }
    }
    return null
  } catch (error) {
    return null
  }
}

export const fetchIp = async (request: Request) => {
  try {
    const { headers: incomingHeaders } = request || {}
    const ipAddress = incomingHeaders?.get('X-Forwarded-For') || ''
    const clientIP = incomingHeaders?.get('X-Forwarded-For')?.split(',')[0] || ''
    const proxyIP = incomingHeaders?.get('X-Forwarded-For')?.split(',')[1] || ''
    const commonHeaders = {
      'CLIENT-USER-IP': clientIP || '',
      PROXY_IP: proxyIP || '',
      X_FORWARDED_FOR: ipAddress,
    }
    return commonHeaders
  } catch (error) {
    return {}
  }
}

export const deleteCookie = (headers: Headers, cookieName: string) => {
  if (cookieName)
    headers?.append('set-cookie', `${cookieName}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT;`)
}

export const deepCopy = (incomingData: any) => {
  try {
    if (incomingData === null || typeof incomingData !== 'object') {
      return incomingData
    }

    const copyOfData = {} as any
    for (const key in incomingData) {
      if (incomingData?.hasOwnProperty(key)) {
        copyOfData[key] = deepCopy(incomingData[key])
      }
    }
    return copyOfData
  } catch (error) {
    return incomingData
  }
}

export const sendDataLayer = (dataObject = {}) => {
  try {
    if (typeof window !== 'undefined') {
      const dataLayerObj = (window as any)?.dataLayer || []
      dataLayerObj?.push({
        ...dataObject,
      })
    }
  } catch (err) {}
}

export const getSFMCCookieData = (moduleName: string) => {
  try {
    if (typeof window !== 'undefined') {
      const sfmcCookie = getObjectCookie('sfmc')
      const valueToSend = !!sfmcCookie?.sfmcActivityId ? { ...sfmcCookie } : null
      const dataLayerObj = {
        testVersion: JSON.stringify({
          ...({ sfmcKey: sfmcCookie?.sfmcActivityId, moduleName: moduleName } || {}),
        }),
        sfmcVersion: JSON.stringify({ sfmc_details: 'sfmc test', ...(valueToSend || {}) }),
      }
      return dataLayerObj
    }
  } catch (err) {}
}
export const parseServerCookie = (cookie: string): Record<string, string> => {
  try {
    if (!cookie) return {}
    return cookie?.split('; ')?.reduce((acc, cookiePart) => {
      const [key, value] = cookiePart?.split('=')
      acc[key] = decodeURIComponent(value || '')
      return acc
    }, {} as Record<string, string>)
  } catch (err) {
    return {}
  }
}

// Derives env suffix from hostname — mirrors the logic in cookie/index.ts so
// client-side deletions always target the correct env-specific cookie name.
const getClientEnvSuffix = () => {
  if (typeof window === 'undefined') return '_dev'
  const h = window.location.hostname
  if (h.includes('develop')) return '_dev'
  if (h.includes('qa')) return '_qa'
  if (h.includes('uat')) return '_uat'
  if (h.includes('mpowerfinancing.com')) return '_prod'
  return '_dev'
}

export const clientDeleteCookie = (cookieList: string[]) => {
  try {
    if (document) {
      const suffix = getClientEnvSuffix()
      cookieList.forEach((cookieName) => {
        // Delete both the suffixed name (new) and the base name (legacy cleanup)
        const suffixedName = cookieName.endsWith(suffix) ? cookieName : `${cookieName}${suffix}`
        document.cookie = `${suffixedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      })
    }
  } catch {}
}
export const getUrlParams = (url: any): { params: Record<string, string>; queryString: string } => {
  try {
    const urlObj = new URL(url)
    const params = urlObj?.searchParams
    const queryParams = url?.searchParams?.toString()
    const decodedUrl = decodeURIComponent(queryParams)
    const result: Record<string, string> = {}
    params.forEach((value, key) => {
      result[key] = value
    })
    const query = decodedUrl
    return {
      params: result,
      queryString: query,
    }
  } catch (err) {
    return {}
  }
}

export const restrictDashboard = (data: Data, url: URLObject): string | false => {
  try {
    const isUserTASorNEW =
      data?.userdetails?.role === USER_ROLE.tas || data?.userdetails?.role === USER_ROLE.preBorrower
    const checkDashboardFlowActive = url?.pathname?.includes('/dashboard')
    switch (true) {
      case isUserTASorNEW && checkDashboardFlowActive:
        return '/eligibility'
      default:
        return // No action if the condition isn't met
    }
  } catch (err) {
    return false
  }
}

export async function getClearServerEndCookie(cookies: any) {
  const headers = new Headers()
  try {
    for (const cookie of cookies) {
      headers?.append('Set-Cookie', await cookie.serialize('', { maxAge: 0 }))
    }
  } catch (err) {}
  return headers
}

export const deleteClientEndCookie = (cookies = [], domain = null) => {
  if (typeof document === 'undefined') return
  const expiredDate = 'Thu, 01 Jan 1970 00:00:00 GMT'
  const suffix = getClientEnvSuffix()
  cookies.forEach((cookieName) => {
    const suffixedName = cookieName.endsWith(suffix) ? cookieName : `${cookieName}${suffix}`
    // Clear suffixed name (current) and base name (legacy cleanup), both with and without domain
    ;[suffixedName, cookieName].forEach((name) => {
      document.cookie = `${name}=; expires=${expiredDate}; path=/;`
      if (domain) {
        document.cookie = `${name}=; path=/; domain=${domain}; expires=${expiredDate};`
      }
    })
  })
}

// Re-export PostHog utilities for backward compatibility
export {
  capturePosthogEvent,
  logGenericPosthogEvent,
  logCustomPosthogEvent,
  capturePageView,
  capturePageLoaded,
  captureButtonClick,
  identifyUser,
  registerProperties,
  unregisterProperty,
  isPosthogReady,
} from './posthog'

export const extractUserDetail = (user: any) => {
  return {
    firstName: UppercaseFirstLetter(user?.userdetails?.firstName),
    lastName: UppercaseFirstLetter(user?.userdetails?.lastName),
    preferedname:
      UppercaseFirstLetter(user?.userdetails?.preferredname) ||
      UppercaseFirstLetter(user?.userdetails?.firstName),
    emailID: user?.userdetails?.email,
    newApp: 'true',
  }
}
export const isUSApplicationCheck = (citizenship_country: any) => {
  return citizenship_country === ApplicationCountry.UNITED_STATE
}
export const isUSSchoolCheck = (school_country: any) => {
  return school_country === ApplicationCountry.UNITED_STATE
}

/** Maps API recordType ("US_Grad_App" | "in_school") to ApplicationType for comparison. */
export const opportunityRecordType = (
  recordType: string | null | undefined,
): ApplicationType.IN_SCHOOL | ApplicationType.US_GRAD | undefined => {
  if (recordType == null) return undefined
  const normalized = String(recordType).trim()
  if (normalized === ApplicationType.IN_SCHOOL) return ApplicationType.IN_SCHOOL
  if (normalized === ApplicationType.US_GRAD) return ApplicationType.US_GRAD
  return undefined
}
export const openExternal = (url?: string): boolean => {
  if (!url) return false

  if (/^https?:\/\//i?.test(url)) {
    window.location.href = url
    return true
  }

  return false
}
